import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import messages from "../../../assets/locale/messages";
import UIStepper from "../../ui/UIStepper/UIStepper";
import { UIButton, UICallout } from "../../ui";
import {
  BasicInformation,
  ChooseCrewSize,
  DefineRolesAndShifts,
  PublishAndPay,
  ZonesAndLogisticalRequirements,
} from "./Steps";
import FormFooter from "./FormFooter";
import PublishEventModal from "./Modals/PublishEventModal";
import {
  COMPANY_PORTAL_FORM_VARIANT,
  getPortalApiErrorMessage,
} from "../../../utils/companyPortal/eventApi";
import { eventStatuses } from "../../../utils/Constants";
import { clx, showErrorMsg } from "../../../utils/Helpers";

import "./_Form.scss";

import { EVENT_FORM_STEP_IDS, isDraftStepDone } from "./formStepIds";
import {
  fillTemplate,
  getEventCrewSizeOption,
  getEventCustomCrewPlan,
  toCrewSizeOption,
} from "./Steps/ChooseCrewSize/crewSizeUtils";

export { EVENT_FORM_STEP_IDS };

const DEFAULT_STEP_IDS = [
  EVENT_FORM_STEP_IDS.basicInfo,
  EVENT_FORM_STEP_IDS.rolesShifts,
  EVENT_FORM_STEP_IDS.zonesRequirements,
];

const STEP_REGISTRY = {
  [EVENT_FORM_STEP_IDS.basicInfo]: {
    Component: BasicInformation,
    getLabel: (creatEvent) => creatEvent.basicInfo,
  },
  [EVENT_FORM_STEP_IDS.crewSize]: {
    Component: ChooseCrewSize,
    getLabel: (creatEvent) => creatEvent.crewSize.stepLabel,
  },
  [EVENT_FORM_STEP_IDS.rolesShifts]: {
    Component: DefineRolesAndShifts,
    getLabel: (creatEvent) => creatEvent.stepTwo,
  },
  [EVENT_FORM_STEP_IDS.zonesRequirements]: {
    Component: ZonesAndLogisticalRequirements,
    getLabel: (creatEvent) => creatEvent.stepThree,
  },
  [EVENT_FORM_STEP_IDS.publishPay]: {
    Component: PublishAndPay,
    getLabel: (creatEvent) => creatEvent.publishPay.stepLabel,
  },
};

const getEventImageUrl =(event) => event?.image_url || "";

const getEventName = (event) => event?.name || "";

const getStaffRequestedCount = (rolesPayload) =>
  (rolesPayload?.event_roles_attributes || []).reduce((total, role) => {
    const males = Number(role?.maximum_number_of_males || 0);
    const females = Number(role?.maximum_number_of_females || 0);
    return total + males + females;
  }, 0);

const CreateForm = ({
  variant,
  isEditMode,
  onSaveDraft,
  onFinish,
  rolesOptions,
  requirementsOptions,
  supervisorsOptions,
  eventTypeOptions,
  eventScheduleBounds,
  initialEvent,
  initialStep,
  requestsPortal,
  eventCompanyId,
  onPersistDraft,
  onPersistStepContinue,
  onFinishPublish,
  onPaymentSubmitted,
  onPaymentRequestFailed,
  onCancel,
  draftLoading,
  nextLoading,
  stepIds,
  canSetCustomCrewSize,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const creatEvent =
    messages[lang]?.events?.creatEvent ?? messages.en.events.creatEvent;
  const sharedUi = messages[lang]?.shared ?? messages.en.shared;

  const isCompanyPortal = variant === COMPANY_PORTAL_FORM_VARIANT;

  /**
   * Admin StepThree parity: only pending drafts show "publish" on the last step.
   * Edit upcoming/ongoing/past/cancelled → save only, no publish confirmation modal.
   */
  const companyPortalShowPublishModal = useMemo(() => {
    if (!isCompanyPortal) return false;
    // A company never publishes directly: Publish & Pay raises a request instead (PROLINE-4016).
    if (requestsPortal === "company") return false;
    if (!isEditMode) return true;
    return initialEvent?.status === eventStatuses.draft;
  }, [isCompanyPortal, isEditMode, initialEvent?.status, requestsPortal]);

  /**
   * "Save As Draft" persists in-progress changes, so the user also needs a way
   * to leave the form without saving. Show Cancel for any event being edited —
   * whether reached via the /edit route (isEditMode) or resumed in the wizard
   * with an already-created event (initialEvent has a status).
   */
  const companyPortalShowCancel =
    isCompanyPortal &&
    !!onCancel &&
    (isEditMode || !!initialEvent?.status);

  /**
   * A published event (upcoming/ongoing/etc.) can't be a draft — the same
   * persist action is labelled "Save" instead of "Save As Draft".
   */
  const companyPortalSaveDraftLabel = useMemo(() => {
    if (!isCompanyPortal || !isEditMode) return undefined;
    if (!initialEvent?.status) return undefined;
    return initialEvent.status !== eventStatuses.draft
      ? creatEvent.save
      : undefined;
  }, [isCompanyPortal, isEditMode, initialEvent?.status, creatEvent.save]);

  const companyPortalLastStepPrimaryLabel = useMemo(() => {
    if (!isCompanyPortal) return undefined;
    return companyPortalShowPublishModal
      ? creatEvent.finishAndPublishEvent
      : creatEvent.finishEvent;
  }, [
    isCompanyPortal,
    companyPortalShowPublishModal,
    creatEvent.finishAndPublishEvent,
    creatEvent.finishEvent,
  ]);

  const steps = useMemo(
    () =>
      stepIds.map((stepId) => ({
        id: stepId,
        label: STEP_REGISTRY[stepId].getLabel(creatEvent),
        Component: STEP_REGISTRY[stepId].Component,
      })),
    [stepIds, creatEvent],
  );

  const [step, setStep] = useState(() =>
    Math.min(Math.max(initialStep, 0), steps.length - 1),
  );

  /**
   * Editing a draft, the steps can be visited in any order: a step shows as done only
   * when the saved event holds what it requires, not because it comes before the open one.
   */
  const isDraftEdit = isEditMode && stepIds.includes(EVENT_FORM_STEP_IDS.crewSize);

  /**
   * Missing fields per step index, found by each step's silent `countMissingFields`, and
   * what a step spells out for the banner (`getSubmitIssues`, e.g. "Add a Supervisor role").
   * Counted for the steps of a failed check (Next or submission).
   */
  // Keyed by step id: the step list grows once a draft loads (Crew Size, Publish & Pay),
  // so an index would point at another step.
  const [missingByStep, setMissingByStep] = useState({});
  const [issuesByStep, setIssuesByStep] = useState({});
  /** Steps that failed the last check (Next or submission); each turns done as it's completed. */
  const [failedSteps, setFailedSteps] = useState([]);
  const [submissionFailedOpen, setSubmissionFailedOpen] = useState(false);
  const hasMissingSteps = failedSteps.length > 0;

  /** The stepper before the missing fields are applied. */
  const baseStepperSteps = useMemo(
    () =>
      isDraftEdit
        ? steps.map((s, index) => {
            let status = "upcoming";
            if (index === step) status = "active";
            else if (
              isDraftStepDone(initialEvent, s.id, {
                requireZoneMap: requestsPortal === "company",
              })
            ) {
              status = "completed";
            }
            return { ...s, status };
          })
        : steps.map((s, index) => ({
            ...s,
            status:
              index === step ? "active" : index < step ? "completed" : "upcoming",
          })),
    [isDraftEdit, steps, step, initialEvent, requestsPortal],
  );

  /** A failed check (Next or submission) marks its steps red until they're complete. */
  const stepperSteps = useMemo(
    () =>
      baseStepperSteps.map((s, index) => {
        if (index === step || !failedSteps.includes(s.id)) return s;
        return missingByStep[s.id]
          ? { ...s, status: "error", hint: issuesByStep[s.id]?.join(" · ") }
          : { ...s, status: "completed" };
      }),
    [baseStepperSteps, step, failedSteps, missingByStep, issuesByStep],
  );
  const [publishModalOpen, setPublishModalOpen] = useState(false);

  /**
   * The step list can grow once the event loads (a draft gets the Crew Size step),
   * so the requested step is applied again against the final list.
   */
  const stepCountRef = useRef(steps.length);
  useEffect(() => {
    if (stepCountRef.current === steps.length) return;
    stepCountRef.current = steps.length;
    setStep(Math.min(Math.max(initialStep, 0), steps.length - 1));
  }, [steps.length, initialStep]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [step]);
  const [pendingPublishPayload, setPendingPublishPayload] = useState(null);
  const [publishSummary, setPublishSummary] = useState({});
  const [publishSubmitting, setPublishSubmitting] = useState(false);
  /** Location boundary (meters) from Basic Info step — seeds paid-training radius */
  const [eventLocationBoundaryMeters, setEventLocationBoundaryMeters] =
    useState("");

  useEffect(() => {
    const r = initialEvent?.event_radius ?? initialEvent?.eventRadius;
    if (r == null || r === "") return;
    setEventLocationBoundaryMeters((prev) => (prev !== "" ? prev : String(r)));
  }, [initialEvent]);

  /** Crew size picked in the Crew Size step; drives the capacity counter in Roles & Shifts. */
  const hasCrewSizeStep = stepIds.includes(EVENT_FORM_STEP_IDS.crewSize);
  /** The API plan picked in the Crew Size step (`event.crew_size_plan` once saved). */
  const [crewSizePlan, setCrewSizePlan] = useState(
    initialEvent?.crew_size_plan ?? null,
  );

  /**
   * Saving any step returns the event with its saved plan. A newly saved plan replaces the
   * selection; the same plan again only refreshes it (name, cost) when it is still selected,
   * so a plan picked on Crew Size and not saved yet stays after going back and forth.
   */
  const savedCrewSizePlanIdRef = useRef(initialEvent?.crew_size_plan?.id);
  useEffect(() => {
    const saved = initialEvent?.crew_size_plan;
    if (!saved) return;
    const isNewlySaved = saved.id !== savedCrewSizePlanIdRef.current;
    savedCrewSizePlanIdRef.current = saved.id;
    setCrewSizePlan((current) =>
      isNewlySaved || current?.id === saved.id ? saved : current,
    );
  }, [initialEvent?.crew_size_plan]);

  /** Custom size and rate the Super Admin sets for more than 300 applicants (raw field values). */
  const [customCrewPlan, setCustomCrewPlan] = useState(() =>
    getEventCustomCrewPlan(initialEvent),
  );

  useEffect(() => {
    if (initialEvent?.custom_crew_size != null) {
      setCustomCrewPlan(getEventCustomCrewPlan(initialEvent));
    }
  }, [initialEvent?.custom_crew_size, initialEvent?.custom_rate]);

  /**
   * Crew size the other steps read. With no Crew Size step (editing a published
   * event) it is the one saved on the event, and its counter is display-only.
   */
  const crewSizeOption = hasCrewSizeStep
    ? toCrewSizeOption(crewSizePlan, customCrewPlan)
    : getEventCrewSizeOption(initialEvent);

  const stepSubmitRef = useRef({});
  const stepApisRef = useRef({});

  const registerStepApi = useCallback((index, api) => {
    if (api == null) {
      delete stepApisRef.current[index];
    } else {
      stepApisRef.current[index] = api;
    }
  }, []);

  const isFirstStep = step === 0;
  const isLastStep = step === steps.length - 1;
  const isPublishPayStep = steps[step]?.id === EVENT_FORM_STEP_IDS.publishPay;
  const publishLoading = isCompanyPortal ? nextLoading : publishSubmitting;

  const buildPublishSummary = useCallback(() => {
    const getStepApi = (stepId) =>
      stepApisRef.current[steps.findIndex((s) => s.id === stepId)];
    const basicSummary =
      getStepApi(EVENT_FORM_STEP_IDS.basicInfo)?.buildSummary?.() || {};
    const rolesPayload =
      getStepApi(EVENT_FORM_STEP_IDS.rolesShifts)?.buildPayload?.() || {};

    return {
      name: basicSummary.name || getEventName(initialEvent),
      image_url: basicSummary.image_url || getEventImageUrl(initialEvent),
      staffRequested: getStaffRequestedCount(rolesPayload),
    };
  }, [initialEvent, steps]);

  const handleBack = () => {
    stepApisRef.current[step]?.resetValidationState?.();
    setStep((currentStep) => Math.max(currentStep - 1, 0));
  };

  // The debounced recount below outlives a render: it reads the current step list.
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  /** Fields still to fill, and what each step spells out, by step id, for the given step indexes. */
  const findMissingFields = async (indexes) => {
    const missing = {};
    const issues = {};
    for (const index of indexes) {
      const api = stepApisRef.current[index];
      // eslint-disable-next-line no-await-in-loop -- each step validates its own form
      const count = await api?.countMissingFields?.();
      const id = stepsRef.current[index]?.id;
      if (count > 0 && id) {
        missing[id] = count;
        const stepIssues = api?.getSubmitIssues?.() ?? [];
        if (stepIssues.length) issues[id] = stepIssues;
      }
    }
    return { missing, issues };
  };

  /** Step whose missing fields are highlighted once it opens (a blocked stepper jump). */
  const revealOnOpenRef = useRef(null);

  /** Saves the open step like "Next" does; false when it isn't valid or the save fails. */
  const persistOpenStep = async () => {
    const api = stepApisRef.current[step];
    if (!api?.validateContinue || !(await api.validateContinue())) return false;
    if (api.skipPersist) return true;
    const payload = await buildStepPayload(api);
    if (!payload) return false;
    const persisted = await onPersistStepContinue?.({
      stepIndex: step,
      payload,
      onError: api?.handlePersistError,
    });
    if (!persisted) return false;
    api?.applyPersistedEvent?.(persisted?.attributes);
    return true;
  };

  /**
   * In edit mode the steps can be opened from the stepper, and after a failed submission
   * the marked steps can be opened too. Going back is free; going ahead needs the steps
   * before the target complete, like "Next": the first incomplete one opens instead and
   * highlights what's missing. Going ahead also saves the open step first, like "Next".
   */
  const handleStepJump = async (_step, index) => {
    if (index === step || !(isEditMode || failedSteps.includes(steps[index]?.id))) {
      return;
    }
    let target = index;
    if (index > step) {
      const before = Array.from({ length: index }, (_, i) => i);
      const { missing, issues } = await findMissingFields(before);
      const blocking = before.find((i) => missing[stepsRef.current[i]?.id]);
      if (blocking !== undefined) {
        // What the step spells out (e.g. the crew size is exceeded), else its fields are missing.
        showErrorMsg(
          issues[stepsRef.current[blocking]?.id]?.join(" ") ||
            creatEvent.submissionFailed.completeStep,
        );
      }
      if (blocking === step) {
        const api = stepApisRef.current[step];
        (api?.revealMissingFields ?? api?.validateContinue)?.();
        return;
      }
      if (blocking !== undefined) {
        revealOnOpenRef.current = stepsRef.current[blocking]?.id;
        target = blocking;
      }
      // Otherwise the open step's edits (e.g. new dates) never reach the backend.
      if (isCompanyPortal && !(await persistOpenStep())) {
        revealOnOpenRef.current = null;
        return;
      }
    }
    stepApisRef.current[step]?.resetValidationState?.();
    setStep(target);
  };

  /**
   * Edits recount the steps of a failed check (debounced): a fixed step is ticked off in the
   * banner and shown as completed, and the banner goes once all are.
   */
  const recountTimerRef = useRef(null);
  const recountIndexesRef = useRef([]);
  recountIndexesRef.current = steps
    .map((s, index) => (failedSteps.includes(s.id) ? index : null))
    .filter((index) => index !== null);
  // Editing, the steps are empty until the event loads into them: nothing to count before.
  const recountReadyRef = useRef(false);
  recountReadyRef.current = !isEditMode || Boolean(initialEvent);
  const recountMissing = useCallback(() => {
    window.clearTimeout(recountTimerRef.current);
    if (!recountReadyRef.current) return;
    recountTimerRef.current = window.setTimeout(async () => {
      const { missing, issues } = await findMissingFields(recountIndexesRef.current);
      setMissingByStep(missing);
      setIssuesByStep(issues);
      if (!Object.keys(missing).length) setSubmissionFailedOpen(false);
    }, 400);
  }, []);
  useEffect(() => () => window.clearTimeout(recountTimerRef.current), []);
  // Also once the event and the role options have loaded into the steps.
  useEffect(() => {
    recountMissing();
  }, [step, initialEvent, rolesOptions, recountMissing]);

  // Opening a step of a failed submission, or one a stepper jump stopped on, highlights its
  // missing fields ("This field is required").
  useEffect(() => {
    const api = stepApisRef.current[step];
    const id = steps[step]?.id;
    const revealOnOpen = revealOnOpenRef.current === id;
    revealOnOpenRef.current = null;
    if (revealOnOpen || (failedSteps.includes(id) && missingByStep[id])) {
      (api?.revealMissingFields ?? api?.validateContinue)?.();
    }
    // Only when the open step changes, not when the counts are refreshed.
  }, [step]);

  /** Lets a step send the user back to another step (e.g. Roles → Crew Size). */
  const handleGoToStep = useCallback(
    (stepId) => {
      const index = steps.findIndex((s) => s.id === stepId);
      if (index < 0 || index === step) return;
      stepApisRef.current[step]?.resetValidationState?.();
      setStep(index);
    },
    [steps, step],
  );

  // buildPayload may upload files (event logo, zone map) and reject when offline;
  // surface the error and resolve `null` so callers abort instead of failing silently.
  const buildStepPayload = async (api, options) => {
    try {
      return (await api?.buildPayload?.(options)) ?? {};
    } catch (e) {
      showErrorMsg(getPortalApiErrorMessage(e, sharedUi.generalErrMsg));
      return null;
    }
  };

  const handleDraftClick = async ({ skipConfirm = false } = {}) => {
    if (isCompanyPortal) {
      const api = stepApisRef.current[step];
      // Steps can ask for a confirmation first (e.g. Publish & Pay popup).
      if (
        !skipConfirm &&
        api?.confirmDraft &&
        !(await api.confirmDraft(buildPublishSummary()))
      ) {
        return;
      }
      if (
        typeof api?.validatePortalDraft === "function" &&
        !(await api.validatePortalDraft())
      ) {
        return;
      }
      const payload = await buildStepPayload(api, { mode: "draft" });
      if (!payload) return;
      const persisted = await onPersistDraft?.({
        stepIndex: step,
        payload,
        onError: api?.handlePersistError,
      });
      api?.applyPersistedEvent?.(persisted?.attributes);
      return;
    }
    await onSaveDraft?.();
  };

  const handleNext = async () => {
    if (isCompanyPortal) {
      const api = stepApisRef.current[step];
      if (!api?.validateContinue || !(await api.validateContinue())) {
        return;
      }
      const payload = await buildStepPayload(api);
      if (!payload) return;
      // Nothing moves on while this step or an earlier one still misses something, e.g. 2 roles
      // with a Supervisor, or a step saved incomplete as a draft ("Save As Draft" isn't checked).
      // On the last step the backend would refuse the event without saying which fields.
      const { missing, issues } = await findMissingFields(
        Array.from({ length: step + 1 }, (_, index) => index),
      );
      setMissingByStep(missing);
      setIssuesByStep(issues);
      setFailedSteps(Object.keys(missing));
      if (Object.keys(missing).length) {
        setSubmissionFailedOpen(true);
        window.scrollTo?.({ top: 0, behavior: "smooth" });
        return;
      }
      setSubmissionFailedOpen(false);
      if (isLastStep) {
        // Publish & Pay raises a publishing request instead of publishing.
        if (api.submitForApproval) {
          await api.submitForApproval(buildPublishSummary());
          return;
        }
        if (companyPortalShowPublishModal) {
          setPendingPublishPayload(payload);
          setPublishSummary(buildPublishSummary());
          setPublishModalOpen(true);
          return;
        }
        await onFinishPublish?.({ stepIndex: step, payload });
        return;
      }
      if (api.skipPersist) {
        setStep((currentStep) => Math.min(currentStep + 1, steps.length - 1));
        return;
      }
      const persisted = await onPersistStepContinue?.({
        stepIndex: step,
        payload,
        onError: api?.handlePersistError,
      });
      if (!persisted) return;
      api?.applyPersistedEvent?.(persisted?.attributes);
      setStep((currentStep) => Math.min(currentStep + 1, steps.length - 1));
      return;
    }

    const validate = stepSubmitRef.current[step];
    if (validate) {
      const canContinue = await validate();
      if (!canContinue) return;
    }

    if (isLastStep) {
      setPublishSummary(buildPublishSummary());
      setPublishModalOpen(true);
      return;
    }

    setStep((currentStep) => Math.min(currentStep + 1, steps.length - 1));
  };

  const handlePublishConfirm = async () => {
    if (isCompanyPortal) {
      // Always close the modal here. On failure the parent shows an error and keeps this route;
      // on success the parent navigates to the events list (after this `finally` runs).
      try {
        const payload =
          pendingPublishPayload ||
          (await buildStepPayload(stepApisRef.current[step]));
        if (!payload) return;
        await onFinishPublish?.({ stepIndex: step, payload });
      } finally {
        setPublishModalOpen(false);
        setPendingPublishPayload(null);
      }
      return;
    }

    setPublishSubmitting(true);
    try {
      await onFinish?.();
      setPublishModalOpen(false);
    } finally {
      setPublishSubmitting(false);
    }
  };

  /** From a banner row to the step that still misses something. */
  const goToMissingStep = (index) => {
    if (index < 0) return;
    stepApisRef.current[step]?.resetValidationState?.();
    setStep(index);
  };

  /** One row per step: what it still misses, or ticked off once complete. */
  const renderMissingList = (stepIdsToList) => (
    <ul className="event-form-container__missing-list">
      {stepIdsToList.map((id) => {
        const index = steps.findIndex((s) => s.id === id);
        const count = missingByStep[id] ?? 0;
        return (
          <li
            key={id}
            className={clx("event-form-container__missing-item", {
              "event-form-container__missing-item--done": !count,
            })}
          >
            <span className="event-form-container__missing-head">
              {count ? (
                <ErrorOutlineIcon fontSize="inherit" aria-hidden="true" />
              ) : (
                <CheckCircleIcon fontSize="inherit" aria-hidden="true" />
              )}
              <span className="event-form-container__missing-label">
                {steps[index]?.label}
              </span>
              {count > 0 && index !== step && (
                <UIButton
                  className="event-form-container__missing-link"
                  variant="text"
                  size="small"
                  type="button"
                  onClick={() => goToMissingStep(index)}
                >
                  {creatEvent.submissionFailed.goToStep}
                </UIButton>
              )}
            </span>
            {count > 0 &&
              issuesByStep[id]?.map((issue) => (
                <span
                  key={issue}
                  className={clx("event-form-container__missing-issue", {
                    // Several things missing in one step: one bullet each.
                    "event-form-container__missing-issue--bullet":
                      issuesByStep[id].length > 1,
                  })}
                >
                  {issue}
                </span>
              ))}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div
      className="event-form-container"
      onInputCapture={recountMissing}
      onClickCapture={recountMissing}
    >
      <UIStepper
        steps={stepperSteps}
        activeStep={step}
        onStepClick={isEditMode || hasMissingSteps ? handleStepJump : undefined}
      />

      {submissionFailedOpen && hasMissingSteps && (
        <UICallout
          variant="error"
          className="event-form-container__submission-failed"
          title={creatEvent.submissionFailed.title}
          description={
            <>
              {creatEvent.submissionFailed.message}
              {renderMissingList(failedSteps)}
            </>
          }
          action={
            <IconButton
              size="small"
              aria-label={sharedUi.close}
              onClick={() => setSubmissionFailedOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      )}

      {steps.map((s, stepIndex) => {
        const StepComponent = s.Component;
        const hidden = step !== stepIndex;
        return (
          <div
            key={s.id}
            className="event-form-step-pane"
            hidden={hidden}
            aria-hidden={hidden}
          >
            {failedSteps.includes(s.id) && missingByStep[s.id] > 0 && (
              <div
                className="event-form-step-pane__missing text-label-md"
                title={issuesByStep[s.id]?.join(" · ")}
              >
                <InfoOutlinedIcon
                  className="event-form-step-pane__missing-icon"
                  aria-hidden="true"
                />
                {fillTemplate(
                  missingByStep[s.id] === 1
                    ? creatEvent.submissionFailed.missingField
                    : creatEvent.submissionFailed.missingFields,
                  { count: missingByStep[s.id] },
                )}
              </div>
            )}
            <StepComponent
              isActive={!hidden}
              isEditMode={isEditMode}
              rolesOptions={rolesOptions}
              requirementsOptions={requirementsOptions}
              supervisorsOptions={supervisorsOptions}
              eventTypeOptions={eventTypeOptions}
              companyPortalMode={isCompanyPortal}
              eventScheduleBounds={eventScheduleBounds}
              initialEvent={initialEvent}
              requestsPortal={requestsPortal}
              eventCompanyId={eventCompanyId}
              onEventLocationBoundarySync={setEventLocationBoundaryMeters}
              eventLocationBoundaryMeters={eventLocationBoundaryMeters}
              registerSubmit={
                isCompanyPortal
                  ? undefined
                  : (handler) => {
                      if (handler) {
                        stepSubmitRef.current[stepIndex] = handler;
                      } else {
                        delete stepSubmitRef.current[stepIndex];
                      }
                    }
              }
              registerStepApi={
                isCompanyPortal
                  ? (api) => registerStepApi(stepIndex, api)
                  : undefined
              }
              crewSizeKey={crewSizePlan?.id ?? null}
              onCrewSizeChange={setCrewSizePlan}
              crewSizeOption={crewSizeOption}
              customCrewPlan={customCrewPlan}
              onCustomCrewPlanChange={setCustomCrewPlan}
              canSetCustomCrewSize={canSetCustomCrewSize}
              onGoToStep={hasCrewSizeStep ? handleGoToStep : undefined}
              onPaymentSubmitted={onPaymentSubmitted}
              onPaymentRequestFailed={onPaymentRequestFailed}
              onSaveAsDraft={() => handleDraftClick({ skipConfirm: true })}
            />
          </div>
        );
      })}

      <FormFooter
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        stepName={steps[step + 1]?.label ?? ""}
        backLabel={sharedUi.back}
        onBack={handleBack}
        onSaveDraft={handleDraftClick}
        onNext={handleNext}
        saveDraftLoading={isCompanyPortal ? draftLoading : false}
        nextLoading={isCompanyPortal ? nextLoading : false}
        draftHint={
          canSetCustomCrewSize ? creatEvent.adminSaveDraftHint : undefined
        }
        lastStepPrimaryLabel={
          isPublishPayStep
            ? creatEvent.publishPay.submitForApproval
            : companyPortalLastStepPrimaryLabel
        }
        saveDraftLabel={companyPortalSaveDraftLabel}
        showSaveDraft={!(isCompanyPortal && isLastStep) || isPublishPayStep}
        onCancel={companyPortalShowCancel ? onCancel : undefined}
        cancelLabel={creatEvent.discardChanges}
      />
      <PublishEventModal
        open={publishModalOpen}
        onClose={() => {
          if (!publishLoading) {
            setPublishModalOpen(false);
          }
        }}
        onPublish={handlePublishConfirm}
        loading={publishLoading}
        eventSummary={publishSummary}
      />
    </div>
  );
};

export default CreateForm;

CreateForm.propTypes = {
  variant: PropTypes.string,
  isEditMode: PropTypes.bool,
  onSaveDraft: PropTypes.func,
  onFinish: PropTypes.func,
  rolesOptions: PropTypes.arrayOf(PropTypes.object),
  requirementsOptions: PropTypes.arrayOf(PropTypes.object),
  supervisorsOptions: PropTypes.arrayOf(PropTypes.object),
  eventTypeOptions: PropTypes.arrayOf(PropTypes.object),
  eventScheduleBounds: PropTypes.shape({
    eventStartAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    eventEndAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  initialEvent: PropTypes.object,
  initialStep: PropTypes.number,
  requestsPortal: PropTypes.oneOf(["admin", "company"]),
  eventCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Called with `{ stepIndex, payload, onError }`; `onError(error)` lets the step show a failed save. */
  onPersistDraft: PropTypes.func,
  onPersistStepContinue: PropTypes.func,
  onFinishPublish: PropTypes.func,
  /** Publish & Pay: the company closed the "Submitted for Approval" popup. */
  onPaymentSubmitted: PropTypes.func,
  /** Publish & Pay: the backend refused the publishing request. */
  onPaymentRequestFailed: PropTypes.func,
  onCancel: PropTypes.func,
  draftLoading: PropTypes.bool,
  nextLoading: PropTypes.bool,
  stepIds: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(EVENT_FORM_STEP_IDS)),
  ),
  /** Super Admin wizard: the Crew Size step offers the Custom plan section. */
  canSetCustomCrewSize: PropTypes.bool,
};

CreateForm.defaultProps = {
  variant: "default",
  isEditMode: false,
  onSaveDraft: undefined,
  onFinish: undefined,
  rolesOptions: [],
  requirementsOptions: [],
  supervisorsOptions: [],
  eventTypeOptions: undefined,
  eventScheduleBounds: undefined,
  initialEvent: undefined,
  initialStep: 0,
  requestsPortal: "admin",
  eventCompanyId: undefined,
  onPersistDraft: undefined,
  onPersistStepContinue: undefined,
  onFinishPublish: undefined,
  onPaymentSubmitted: undefined,
  onPaymentRequestFailed: undefined,
  onCancel: undefined,
  draftLoading: false,
  nextLoading: false,
  stepIds: DEFAULT_STEP_IDS,
  canSetCustomCrewSize: false,
};
